import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { assetUrl } from "../../api/http";
import {
  VirtualTryOnBounds,
  VirtualTryOnConfig,
  VirtualTryOnPhysicalMeasurement,
  VirtualTryOnPhotoRegion,
  VirtualTryOnPoint,
} from "../../types/products";

type AnchorKey =
  | "leftChain"
  | "rightChain"
  | "pendantTop"
  | "pendantCenter"
  | "pendantBottom";
type RegionKey =
  | "fullAssetBounds"
  | "pendantBounds"
  | "pendantBodyBounds"
  | "photoRegion";
type EditMode =
  | "points"
  | "fullAssetBounds"
  | "pendantBounds"
  | "pendantBodyBounds"
  | "photoRegion"
  | "measurement";
type DragTarget =
  | { type: "anchor"; key: AnchorKey }
  | { type: "measurement"; key: "start" | "end" }
  | { type: "measurementLine" }
  | { type: "region"; key: RegionKey; mode: "move" | "resize" };

type Props = {
  imageUrl?: string;
  value?: VirtualTryOnConfig;
  onChange: (next: VirtualTryOnConfig) => void;
};

const fallbackAnchors: Required<NonNullable<VirtualTryOnConfig["anchors"]>> = {
  leftChain: { x: 0.12, y: 0.08 },
  rightChain: { x: 0.88, y: 0.08 },
  pendantTop: { x: 0.5, y: 0.56 },
  pendantCenter: { x: 0.5, y: 0.72 },
  pendantBottom: { x: 0.5, y: 0.9 },
};

const fallbackFullAssetBounds: VirtualTryOnBounds = {
  x: 0.08,
  y: 0.04,
  width: 0.84,
  height: 0.88,
};

const fallbackPendantBounds: VirtualTryOnBounds = {
  x: 0.36,
  y: 0.56,
  width: 0.28,
  height: 0.32,
};

const fallbackPhotoRegion: VirtualTryOnPhotoRegion = {
  shape: "circle",
  x: 0.4,
  y: 0.64,
  width: 0.2,
  height: 0.2,
  rotation: 0,
};

const fallbackPhysicalMeasurement: VirtualTryOnPhysicalMeasurement = {
  start: { x: 0.39, y: 0.74 },
  end: { x: 0.61, y: 0.74 },
  value: 54,
  unit: "mm",
  measurementType: "width",
};

const anchorMeta: Array<{
  key: AnchorKey;
  label: string;
  name: string;
}> = [
  { key: "leftChain", label: "L", name: "Left chain-to-neck anchor" },
  { key: "rightChain", label: "R", name: "Right chain-to-neck anchor" },
  { key: "pendantTop", label: "T", name: "Pendant top / bail anchor" },
  { key: "pendantCenter", label: "C", name: "Pendant center" },
  { key: "pendantBottom", label: "B", name: "Pendant bottom" },
];

const editModes: Array<{
  key: EditMode;
  label: string;
  description: string;
}> = [
  {
    key: "points",
    label: "Anchors",
    description: "Move chain, bail, center, and bottom points",
  },
  {
    key: "fullAssetBounds",
    label: "Full PNG",
    description: "The visible chain plus pendant area inside the transparent PNG",
  },
  {
    key: "pendantBounds",
    label: "Legacy bounds",
    description: "Older fallback pendant box kept for compatibility",
  },
  {
    key: "pendantBodyBounds",
    label: "Pendant body",
    description: "The actual pendant body, excluding chain and transparent padding",
  },
  {
    key: "photoRegion",
    label: "Photo area",
    description: "Where the customer photo or engraving appears inside the pendant",
  },
  {
    key: "measurement",
    label: "Measurement line",
    description: "Draw or move the real-size reference line",
  },
];

const measurementPresets: Array<{
  value: NonNullable<VirtualTryOnPhysicalMeasurement["measurementType"]>;
  label: string;
}> = [
  { value: "width", label: "Pendant width" },
  { value: "height", label: "Pendant height" },
  { value: "diameter", label: "Circle diameter" },
  { value: "wingSpan", label: "Wing span" },
  { value: "custom", label: "Custom" },
];

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const clampSize = (value: number) => Math.min(1, Math.max(0.02, value));
const clampScaleCorrection = (value: number) =>
  Math.min(4, Math.max(0.25, Number.isFinite(value) && value > 0 ? value : 1));
const INCH_TO_MM = 25.4;
const DEFAULT_TRY_ON_WEAR_LIFT_MM = 26;
const MAX_INCLUDED_CHAIN_WEAR_DROP_CORRECTION_MM = 35;
const MAX_LOOSE_CHAIN_WEAR_DROP_CORRECTION_MM = 90;
const SAMPLE_TEST_PHOTO_URL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' x2='1' y1='0' y2='1'%3E%3Cstop stop-color='%23fef3c7'/%3E%3Cstop offset='1' stop-color='%23bfdbfe'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='400' fill='url(%23g)'/%3E%3Ccircle cx='200' cy='150' r='72' fill='%23f8fafc'/%3E%3Cpath d='M70 360c24-88 75-132 130-132s106 44 130 132' fill='%23f8fafc'/%3E%3Ccircle cx='176' cy='144' r='9' fill='%230f172a'/%3E%3Ccircle cx='224' cy='144' r='9' fill='%230f172a'/%3E%3Cpath d='M166 188c21 22 47 22 68 0' fill='none' stroke='%230f172a' stroke-width='12' stroke-linecap='round'/%3E%3C/svg%3E";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/latest/pose_landmarker_lite.task";
const WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const TASKS_VISION_MODULE_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/+esm";
const POSE_LEFT_SHOULDER = 11;
const POSE_RIGHT_SHOULDER = 12;

type PoseLandmarkLike = {
  x: number;
  y: number;
  visibility?: number;
  presence?: number;
};

type PoseLandmarkerLike = {
  detectForVideo: (video: HTMLVideoElement, timestamp: number) => {
    landmarks?: PoseLandmarkLike[][];
  };
  close?: () => void;
};

type TasksVisionModuleLike = {
  FilesetResolver: {
    forVisionTasks: (wasmUrl: string) => Promise<unknown>;
  };
  PoseLandmarker: {
    createFromOptions: (
      fileset: unknown,
      options: {
        baseOptions: { modelAssetPath: string; delegate?: "GPU" | "CPU" };
        runningMode: "VIDEO";
        numPoses: number;
        minPoseDetectionConfidence: number;
        minPosePresenceConfidence: number;
        minTrackingConfidence: number;
        outputSegmentationMasks: boolean;
      }
    ) => Promise<PoseLandmarkerLike>;
  };
};

type LiveScaleSession = {
  samples: Array<{ shoulderWidthPx: number; cameraPixelsPerMm: number }>;
  baselineShoulderWidthPx: number;
  baselineCameraPixelsPerMm: number;
  baselinePhysicalScale: number;
  smoothedShoulderWidthPx: number;
  depthRatio: number;
};

type LiveCalibrationDebug = {
  configuredMm: number;
  assetMeasurementPx: number;
  assetPixelsPerMm: number;
  rawCameraPixelsPerMm: number;
  rawImageScale: number;
  physicalScaleCorrection: number;
  correctedBaseScale: number;
  baselineShoulderWidthPx: number;
  currentShoulderWidthPx: number;
  depthRatio: number;
  finalRenderedScale: number;
  renderedMeasurementPx: number;
};

const normalizePoint = (point?: VirtualTryOnPoint): VirtualTryOnPoint | undefined => {
  if (!point) return undefined;
  const x = Number(point.x);
  const y = Number(point.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
  return { x: clamp01(x), y: clamp01(y) };
};

const normalizeBounds = (
  bounds?: Partial<VirtualTryOnBounds>,
  fallback = fallbackPendantBounds
): VirtualTryOnBounds => {
  const width = clampSize(Number(bounds?.width ?? fallback.width));
  const height = clampSize(Number(bounds?.height ?? fallback.height));
  const x = clamp01(Math.min(Number(bounds?.x ?? fallback.x), 1 - width));
  const y = clamp01(Math.min(Number(bounds?.y ?? fallback.y), 1 - height));
  return { x, y, width, height };
};

const normalizePhotoRegion = (
  region?: Partial<VirtualTryOnPhotoRegion>
): VirtualTryOnPhotoRegion => {
  const bounds = normalizeBounds(region, fallbackPhotoRegion);
  const shape =
    region?.shape === "ellipse" ||
    region?.shape === "rect" ||
    region?.shape === "circle" ||
    region?.shape === "custom"
      ? region.shape
      : fallbackPhotoRegion.shape;
  const rotation = Number(region?.rotation ?? 0);
  return {
    shape,
    ...bounds,
    rotation: Number.isFinite(rotation) ? rotation : 0,
  };
};

const normalizePhysicalMeasurement = (
  measurement?: Partial<VirtualTryOnPhysicalMeasurement>
): VirtualTryOnPhysicalMeasurement | undefined => {
  const start = normalizePoint(measurement?.start);
  const end = normalizePoint(measurement?.end);
  const value = Number(measurement?.value);
  const unit = measurement?.unit === "in" ? "in" : "mm";
  if (!start || !end || !Number.isFinite(value) || value <= 0) return undefined;
  const measurementType = measurementPresets.some(
    (preset) => preset.value === measurement?.measurementType
  )
    ? measurement?.measurementType
    : "custom";
  const assetPixelsPerMm = Number(measurement?.assetPixelsPerMm);
  return {
    start,
    end,
    value,
    unit,
    measurementType,
    assetPixelsPerMm:
      Number.isFinite(assetPixelsPerMm) && assetPixelsPerMm > 0
        ? assetPixelsPerMm
        : undefined,
  };
};

const measurementMm = (measurement?: VirtualTryOnPhysicalMeasurement) => {
  if (!measurement) return 0;
  return measurement.unit === "in"
    ? measurement.value * INCH_TO_MM
    : measurement.value;
};

const displayMeasurement = (measurement?: VirtualTryOnPhysicalMeasurement) => {
  if (!measurement) return "No physical reference";
  const suffix = measurement.unit === "in" ? "in" : "mm";
  return `${measurement.value.toFixed(measurement.unit === "in" ? 2 : 1)} ${suffix} reference`;
};

const measurementLinePx = (
  measurement: VirtualTryOnPhysicalMeasurement | undefined,
  imageWidthPx?: number,
  imageHeightPx?: number
) => {
  if (!measurement || !imageWidthPx || !imageHeightPx) return 0;
  return Math.hypot(
    (measurement.end.x - measurement.start.x) * imageWidthPx,
    (measurement.end.y - measurement.start.y) * imageHeightPx
  );
};

const assetPixelsPerMmFor = (
  measurement: VirtualTryOnPhysicalMeasurement | undefined,
  imageWidthPx?: number,
  imageHeightPx?: number
) => {
  if (!measurement) return 0;
  const linePx = measurementLinePx(measurement, imageWidthPx, imageHeightPx);
  const mm = measurementMm(measurement);
  if (linePx > 0 && mm > 0) return linePx / mm;
  const stored = Number(measurement.assetPixelsPerMm);
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
};

const rectContainsRect = (
  outer: VirtualTryOnBounds,
  inner: VirtualTryOnBounds
) =>
  inner.x >= outer.x &&
  inner.y >= outer.y &&
  inner.x + inner.width <= outer.x + outer.width &&
  inner.y + inner.height <= outer.y + outer.height;

const lineIntersectsRect = (
  measurement: VirtualTryOnPhysicalMeasurement,
  rect: VirtualTryOnBounds
) => {
  const minX = rect.x;
  const maxX = rect.x + rect.width;
  const minY = rect.y;
  const maxY = rect.y + rect.height;
  const containsPoint = (point: VirtualTryOnPoint) =>
    point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  if (containsPoint(measurement.start) || containsPoint(measurement.end)) {
    return true;
  }
  const intersectsSegment = (
    a: VirtualTryOnPoint,
    b: VirtualTryOnPoint,
    c: VirtualTryOnPoint,
    d: VirtualTryOnPoint
  ) => {
    const ccw = (
      p1: VirtualTryOnPoint,
      p2: VirtualTryOnPoint,
      p3: VirtualTryOnPoint
    ) => (p3.y - p1.y) * (p2.x - p1.x) > (p2.y - p1.y) * (p3.x - p1.x);
    return (
      ccw(a, c, d) !== ccw(b, c, d) &&
      ccw(a, b, c) !== ccw(a, b, d)
    );
  };
  const corners = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ];
  return corners.some((corner, index) =>
    intersectsSegment(
      measurement.start,
      measurement.end,
      corner,
      corners[(index + 1) % corners.length]
    )
  );
};

const getAnchors = (value?: VirtualTryOnConfig) => ({
  leftChain: normalizePoint(value?.anchors?.leftChain) || fallbackAnchors.leftChain,
  rightChain: normalizePoint(value?.anchors?.rightChain) || fallbackAnchors.rightChain,
  pendantTop: normalizePoint(value?.anchors?.pendantTop) || fallbackAnchors.pendantTop,
  pendantCenter:
    normalizePoint(value?.anchors?.pendantCenter) || fallbackAnchors.pendantCenter,
  pendantBottom:
    normalizePoint(value?.anchors?.pendantBottom) || fallbackAnchors.pendantBottom,
});

const pointDistance = (a: VirtualTryOnPoint, b: VirtualTryOnPoint) =>
  Math.hypot(b.x - a.x, b.y - a.y);

const landmarkConfidence = (landmark?: PoseLandmarkLike) => {
  if (!landmark) return 0;
  const visibility =
    typeof landmark.visibility === "number" ? landmark.visibility : 1;
  const presence = typeof landmark.presence === "number" ? landmark.presence : 1;
  return Math.min(visibility, presence);
};

const medianNumber = (values: number[]) => {
  const sorted = values
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

function getVideoCoverRect(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
  return calculateCameraDisplayRect({
    videoWidth: video.videoWidth || canvas.width || 1,
    videoHeight: video.videoHeight || canvas.height || 1,
    displayWidth: canvas.width,
    displayHeight: canvas.height,
  });
}

function calculateCameraDisplayRect({
  videoWidth,
  videoHeight,
  displayWidth,
  displayHeight,
}: {
  videoWidth: number;
  videoHeight: number;
  displayWidth: number;
  displayHeight: number;
}) {
  const safeVideoWidth = Math.max(Number(videoWidth) || 0, 1);
  const safeVideoHeight = Math.max(Number(videoHeight) || 0, 1);
  const safeDisplayWidth = Math.max(Number(displayWidth) || 0, 1);
  const safeDisplayHeight = Math.max(Number(displayHeight) || 0, 1);
  const scale = Math.max(
    safeDisplayWidth / safeVideoWidth,
    safeDisplayHeight / safeVideoHeight
  );
  const width = safeVideoWidth * scale;
  const height = safeVideoHeight * scale;
  return {
    x: (safeDisplayWidth - width) / 2,
    y: (safeDisplayHeight - height) / 2,
    width,
    height,
    scale,
    cropX: (width - safeDisplayWidth) / 2,
    cropY: (height - safeDisplayHeight) / 2,
  };
}

function mapCameraPointToDisplay(
  landmark: Pick<PoseLandmarkLike, "x" | "y">,
  rect: { x: number; y: number; width: number; height: number },
  mirrored = true
) {
  return {
    x: rect.x + (mirrored ? 1 - landmark.x : landmark.x) * rect.width,
    y: rect.y + landmark.y * rect.height,
  };
}

function drawMirroredCalibrationVideo(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
  rect = getVideoCoverRect(canvas, video)
) {
  ctx.save();
  ctx.translate(canvas.width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(
    video,
    canvas.width - rect.x - rect.width,
    rect.y,
    rect.width,
    rect.height
  );
  ctx.restore();
}

function updateLiveScaleSession(
  session: LiveScaleSession,
  {
    shoulderWidthPx,
    cameraPixelsPerMm,
    assetPixelsPerMm,
    correction,
  }: {
    shoulderWidthPx: number;
    cameraPixelsPerMm: number;
    assetPixelsPerMm: number;
    correction: number;
  }
) {
  const nextSession = session;
  if (!nextSession.baselineShoulderWidthPx) {
    nextSession.samples = [
      ...nextSession.samples.slice(-23),
      { shoulderWidthPx, cameraPixelsPerMm },
    ];
    if (nextSession.samples.length >= 24) {
      nextSession.baselineShoulderWidthPx = medianNumber(
        nextSession.samples.map((sample) => sample.shoulderWidthPx)
      );
      nextSession.baselineCameraPixelsPerMm = medianNumber(
        nextSession.samples.map((sample) => sample.cameraPixelsPerMm)
      );
      nextSession.baselinePhysicalScale =
        (nextSession.baselineCameraPixelsPerMm / assetPixelsPerMm) *
        correction;
      nextSession.smoothedShoulderWidthPx = nextSession.baselineShoulderWidthPx;
      nextSession.depthRatio = 1;
    }
    return nextSession;
  }
  nextSession.baselinePhysicalScale =
    (nextSession.baselineCameraPixelsPerMm / assetPixelsPerMm) * correction;
  const previous = nextSession.smoothedShoulderWidthPx || shoulderWidthPx;
  const jump = Math.abs(shoulderWidthPx - previous) / Math.max(previous, 1);
  if (jump <= 0.22) {
    nextSession.smoothedShoulderWidthPx =
      previous + (shoulderWidthPx - previous) * 0.24;
  }
  const rawDepthRatio =
    nextSession.smoothedShoulderWidthPx / nextSession.baselineShoulderWidthPx;
  if (Math.abs(rawDepthRatio - nextSession.depthRatio) > 0.015) {
    nextSession.depthRatio = Math.min(2.25, Math.max(0.45, rawDepthRatio));
  }
  return nextSession;
}

async function loadPoseLandmarker() {
  const vision = (await import(
    /* webpackIgnore: true */ TASKS_VISION_MODULE_URL
  )) as TasksVisionModuleLike;
  const fileset = await vision.FilesetResolver.forVisionTasks(WASM_URL);
  try {
    return await vision.PoseLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.55,
      minPosePresenceConfidence: 0.55,
      minTrackingConfidence: 0.55,
      outputSegmentationMasks: false,
    });
  } catch {
    return vision.PoseLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: "CPU" },
      runningMode: "VIDEO",
      numPoses: 1,
      minPoseDetectionConfidence: 0.55,
      minPosePresenceConfidence: 0.55,
      minTrackingConfidence: 0.55,
      outputSegmentationMasks: false,
    });
  }
}

function loadCalibrationImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load try-on asset."));
    image.src = src;
  });
}

export default function VirtualTryOnAnchorEditor({
  imageUrl,
  value,
  onChange,
}: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const liveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const livePoseRef = useRef<PoseLandmarkerLike | null>(null);
  const liveAssetImageRef = useRef<HTMLImageElement | null>(null);
  const liveAnimationRef = useRef<number | null>(null);
  const liveValueRef = useRef<VirtualTryOnConfig | undefined>(value);
  const liveCorrectionRef = useRef(1);
  const lastLiveDebugAtRef = useRef(0);
  const liveScaleSessionRef = useRef<LiveScaleSession>({
    samples: [],
    baselineShoulderWidthPx: 0,
    baselineCameraPixelsPerMm: 0,
    baselinePhysicalScale: 0,
    smoothedShoulderWidthPx: 0,
    depthRatio: 1,
  });
  const autoAppliedImageUrlRef = useRef("");
  const testPhotoObjectUrlRef = useRef("");
  const dragStartRef = useRef<{
    pointer: VirtualTryOnPoint;
    bounds: VirtualTryOnBounds;
    measurement?: VirtualTryOnPhysicalMeasurement;
  } | null>(null);
  const drawingStartRef = useRef<VirtualTryOnPoint | null>(null);
  const [imageAnalysis, setImageAnalysis] = useState<{
    imageWidthPx: number;
    imageHeightPx: number;
    opaqueBounds?: VirtualTryOnBounds;
    error?: string;
  } | null>(null);
  const [drawingMeasurement, setDrawingMeasurement] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>("points");
  const [testPhotoUrl, setTestPhotoUrl] = useState(SAMPLE_TEST_PHOTO_URL);
  const [liveCalibrationActive, setLiveCalibrationActive] = useState(false);
  const [liveCalibrationError, setLiveCalibrationError] = useState("");
  const [liveCalibrationLoading, setLiveCalibrationLoading] = useState("");
  const [liveScaleCorrection, setLiveScaleCorrection] = useState(
    clampScaleCorrection(Number(value?.physicalScaleCorrection ?? 1))
  );
  const [liveDebug, setLiveDebug] = useState<LiveCalibrationDebug | null>(null);

  useEffect(() => {
    if (!liveCalibrationActive) {
      setLiveScaleCorrection(
        clampScaleCorrection(Number(value?.physicalScaleCorrection ?? 1))
      );
    }
  }, [liveCalibrationActive, value?.physicalScaleCorrection]);

  useEffect(() => {
    liveValueRef.current = value;
  }, [value]);

  useEffect(() => {
    liveCorrectionRef.current = liveScaleCorrection;
  }, [liveScaleCorrection]);
  const anchors = useMemo(() => getAnchors(value), [value]);
  const fullAssetBounds = useMemo(
    () =>
      normalizeBounds(
        value?.fullAssetBounds || value?.opaqueBounds,
        imageAnalysis?.opaqueBounds || fallbackFullAssetBounds
      ),
    [imageAnalysis?.opaqueBounds, value?.fullAssetBounds, value?.opaqueBounds]
  );
  const pendantBounds = useMemo(
    () => normalizeBounds(value?.pendantBounds),
    [value?.pendantBounds]
  );
  const pendantBodyBounds = useMemo(
    () =>
      normalizeBounds(
        value?.pendantBodyBounds,
        value?.pendantBounds || fallbackPendantBounds
      ),
    [value?.pendantBodyBounds, value?.pendantBounds]
  );
  const photoRegion = useMemo(
    () => normalizePhotoRegion(value?.photoRegion),
    [value?.photoRegion]
  );
  const physicalMeasurement = useMemo(
    () => normalizePhysicalMeasurement(value?.physicalMeasurement),
    [value?.physicalMeasurement]
  );
  const resolvedImageWidthPx =
    Number(value?.imageWidthPx) || imageAnalysis?.imageWidthPx || 0;
  const resolvedImageHeightPx =
    Number(value?.imageHeightPx) || imageAnalysis?.imageHeightPx || 0;
  const calculatedAssetPixelsPerMm = assetPixelsPerMmFor(
    physicalMeasurement,
    resolvedImageWidthPx,
    resolvedImageHeightPx
  );

  const nextBase = useCallback(
    (): VirtualTryOnConfig => ({
      enabled: value?.enabled ?? false,
      assetUrl: value?.assetUrl,
      label: value?.label,
      color: value?.color,
      side: value?.side,
      anchors,
      imageWidthPx:
        Number(value?.imageWidthPx) ||
        imageAnalysis?.imageWidthPx ||
        undefined,
      imageHeightPx:
        Number(value?.imageHeightPx) ||
        imageAnalysis?.imageHeightPx ||
        undefined,
      opaqueBounds: value?.opaqueBounds || imageAnalysis?.opaqueBounds,
      fullAssetBounds,
      pendantBounds,
      pendantBodyBounds,
      photoRegion,
      photoCompositionMode: value?.photoCompositionMode ?? "replace-region",
      photoClipping: value?.photoClipping ?? true,
      frameOverlayUrl: value?.frameOverlayUrl,
      physicalMeasurement,
      assetPixelsPerMm:
        calculatedAssetPixelsPerMm ||
        Number(value?.assetPixelsPerMm) ||
        undefined,
      physicalScaleCorrection: clampScaleCorrection(
        Number(value?.physicalScaleCorrection ?? 1)
      ),
      physicalScaleCalibratedAt: value?.physicalScaleCalibratedAt,
      physicalScaleCalibrationNote: value?.physicalScaleCalibrationNote,
      chainIncluded: value?.chainIncluded ?? true,
      defaultScale: value?.defaultScale ?? 1,
      verticalOffset: value?.verticalOffset ?? 0,
      horizontalOffset: value?.horizontalOffset ?? 0,
      rotationOffset: value?.rotationOffset ?? 0,
      defaultVerticalOffset: value?.defaultVerticalOffset ?? value?.verticalOffset ?? 0,
      defaultHorizontalOffset: value?.defaultHorizontalOffset ?? value?.horizontalOffset ?? 0,
      defaultRotationOffset: value?.defaultRotationOffset ?? value?.rotationOffset ?? 0,
      pendantWidthMm: value?.pendantWidthMm,
      pendantHeightMm: value?.pendantHeightMm,
      pendantThicknessMm: value?.pendantThicknessMm,
      chainLengthMm: value?.chainLengthMm,
      chainLengthInches: value?.chainLengthInches,
      wearOffsetXMm: value?.wearOffsetXMm,
      wearOffsetYMm: value?.wearOffsetYMm,
      wearDropMm: value?.wearDropMm,
      wearRotationDeg: value?.wearRotationDeg,
    }),
    [
      anchors,
      calculatedAssetPixelsPerMm,
      fullAssetBounds,
      imageAnalysis?.imageHeightPx,
      imageAnalysis?.imageWidthPx,
      imageAnalysis?.opaqueBounds,
      pendantBodyBounds,
      pendantBounds,
      photoRegion,
      physicalMeasurement,
      value,
    ]
  );

  useEffect(() => {
    if (!imageUrl) {
      setImageAnalysis(null);
      autoAppliedImageUrlRef.current = "";
      return undefined;
    }
    let cancelled = false;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (cancelled) return;
      const imageWidthPx = image.naturalWidth || image.width || 0;
      const imageHeightPx = image.naturalHeight || image.height || 0;
      let opaqueBounds: VirtualTryOnBounds | undefined;
      let error: string | undefined;
      if (imageWidthPx > 0 && imageHeightPx > 0) {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = imageWidthPx;
          canvas.height = imageHeightPx;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas unavailable.");
          ctx.drawImage(image, 0, 0);
          const data = ctx.getImageData(0, 0, imageWidthPx, imageHeightPx).data;
          let minX = imageWidthPx;
          let minY = imageHeightPx;
          let maxX = -1;
          let maxY = -1;
          for (let y = 0; y < imageHeightPx; y += 1) {
            for (let x = 0; x < imageWidthPx; x += 1) {
              const alpha = data[(y * imageWidthPx + x) * 4 + 3];
              if (alpha <= 8) continue;
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
          if (maxX >= minX && maxY >= minY) {
            opaqueBounds = {
              x: minX / imageWidthPx,
              y: minY / imageHeightPx,
              width: (maxX - minX + 1) / imageWidthPx,
              height: (maxY - minY + 1) / imageHeightPx,
            };
          }
        } catch {
          error = "Image dimensions loaded, but transparent bounds could not be inspected.";
        }
      }
      const nextAnalysis = { imageWidthPx, imageHeightPx, opaqueBounds, error };
      setImageAnalysis(nextAnalysis);
      if (autoAppliedImageUrlRef.current === imageUrl) return;
      autoAppliedImageUrlRef.current = imageUrl;
      const shouldApply =
        !value?.imageWidthPx ||
        !value?.imageHeightPx ||
        (!value?.opaqueBounds && opaqueBounds);
      if (shouldApply) {
        onChange({
          ...nextBase(),
          imageWidthPx: imageWidthPx || value?.imageWidthPx,
          imageHeightPx: imageHeightPx || value?.imageHeightPx,
          opaqueBounds: value?.opaqueBounds || opaqueBounds,
          fullAssetBounds: value?.fullAssetBounds || opaqueBounds || fullAssetBounds,
          pendantBodyBounds:
            value?.pendantBodyBounds || value?.pendantBounds || pendantBodyBounds,
        });
      }
    };
    image.onerror = () => {
      if (!cancelled) {
        setImageAnalysis({
          imageWidthPx: Number(value?.imageWidthPx) || 0,
          imageHeightPx: Number(value?.imageHeightPx) || 0,
          error: "Unable to inspect this image.",
        });
      }
    };
    image.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [
    fullAssetBounds,
    imageUrl,
    nextBase,
    onChange,
    pendantBodyBounds,
    value?.fullAssetBounds,
    value?.imageHeightPx,
    value?.imageWidthPx,
    value?.opaqueBounds,
    value?.pendantBodyBounds,
    value?.pendantBounds,
  ]);

  useEffect(
    () => () => {
      if (testPhotoObjectUrlRef.current) {
        URL.revokeObjectURL(testPhotoObjectUrlRef.current);
        testPhotoObjectUrlRef.current = "";
      }
    },
    []
  );

  const pointFromEvent = (event: React.PointerEvent) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
    };
  };

  const updateAnchor = (key: AnchorKey, point: VirtualTryOnPoint) => {
    onChange({
      ...nextBase(),
      anchors: {
        ...anchors,
        [key]: point,
      },
    });
  };

  const updateMeasurement = (
    next: VirtualTryOnPhysicalMeasurement | undefined
  ) => {
    const assetPixelsPerMm = assetPixelsPerMmFor(
      next,
      resolvedImageWidthPx,
      resolvedImageHeightPx
    );
    onChange({
      ...nextBase(),
      physicalMeasurement:
        next && assetPixelsPerMm > 0
          ? { ...next, assetPixelsPerMm }
          : next,
      assetPixelsPerMm: assetPixelsPerMm > 0 ? assetPixelsPerMm : undefined,
    });
  };

  const updateRegion = (key: RegionKey, bounds: VirtualTryOnBounds) => {
    if (key === "fullAssetBounds") {
      onChange({
        ...nextBase(),
        fullAssetBounds: bounds,
      });
      return;
    }
    if (key === "pendantBounds") {
      onChange({
        ...nextBase(),
        pendantBounds: bounds,
      });
      return;
    }
    if (key === "pendantBodyBounds") {
      onChange({
        ...nextBase(),
        pendantBodyBounds: bounds,
      });
      return;
    }
    onChange({
      ...nextBase(),
      photoRegion: {
        ...photoRegion,
        ...bounds,
      },
    });
  };

  const boundsForRegion = (key: RegionKey) => {
    if (key === "fullAssetBounds") return fullAssetBounds;
    if (key === "pendantBounds") return pendantBounds;
    if (key === "pendantBodyBounds") return pendantBodyBounds;
    return photoRegion;
  };

  const canDrag = (target: DragTarget) => {
    if (target.type === "anchor") return editMode === "points";
    if (target.type === "measurement") {
      return editMode === "points" || editMode === "measurement";
    }
    if (target.type === "measurementLine") return editMode === "measurement";
    return editMode === target.key;
  };

  const beginDrag = (target: DragTarget, event: React.PointerEvent<Element>) => {
    if (!canDrag(target)) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointFromEvent(event);
    if (!point) return;
    if (target.type === "anchor") {
      updateAnchor(target.key, point);
      return;
    }
    if (target.type === "measurement") {
      const current = physicalMeasurement || fallbackPhysicalMeasurement;
      updateMeasurement({
        ...current,
        [target.key]: point,
      });
      return;
    }
    if (target.type === "measurementLine") {
      dragStartRef.current = {
        pointer: point,
        bounds: fallbackPendantBounds,
        measurement: physicalMeasurement || fallbackPhysicalMeasurement,
      };
      return;
    }
    dragStartRef.current = {
      pointer: point,
      bounds: boundsForRegion(target.key),
    };
  };

  const continueDrag = (
    target: DragTarget,
    event: React.PointerEvent<Element>
  ) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const point = pointFromEvent(event);
    if (!point) return;
    if (target.type === "anchor") {
      updateAnchor(target.key, point);
      return;
    }
    if (target.type === "measurement") {
      const current = physicalMeasurement || fallbackPhysicalMeasurement;
      updateMeasurement({
        ...current,
        [target.key]: point,
      });
      return;
    }
    const start = dragStartRef.current;
    if (!start) return;
    const dx = point.x - start.pointer.x;
    const dy = point.y - start.pointer.y;
    if (target.type === "measurementLine") {
      const current = start.measurement || physicalMeasurement || fallbackPhysicalMeasurement;
      const width = current.end.x - current.start.x;
      const height = current.end.y - current.start.y;
      const nextStart = {
        x: clamp01(current.start.x + dx),
        y: clamp01(current.start.y + dy),
      };
      let nextEnd = {
        x: clamp01(current.end.x + dx),
        y: clamp01(current.end.y + dy),
      };
      if (nextStart.x === 0 || nextStart.x === 1) {
        nextEnd.x = clamp01(nextStart.x + width);
      }
      if (nextStart.y === 0 || nextStart.y === 1) {
        nextEnd.y = clamp01(nextStart.y + height);
      }
      updateMeasurement({
        ...current,
        start: nextStart,
        end: nextEnd,
      });
      return;
    }
    if (target.mode === "resize") {
      const width = clampSize(start.bounds.width + dx);
      const height = clampSize(start.bounds.height + dy);
      updateRegion(target.key, {
        x: clamp01(Math.min(start.bounds.x, 1 - width)),
        y: clamp01(Math.min(start.bounds.y, 1 - height)),
        width,
        height,
      });
      return;
    }
    updateRegion(target.key, {
      ...start.bounds,
      x: clamp01(Math.min(start.bounds.x + dx, 1 - start.bounds.width)),
      y: clamp01(Math.min(start.bounds.y + dy, 1 - start.bounds.height)),
    });
  };

  const endDrag = () => {
    dragStartRef.current = null;
    drawingStartRef.current = null;
  };

  const beginDrawMeasurement = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drawingMeasurement || editMode !== "measurement") return;
    event.preventDefault();
    const point = pointFromEvent(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingStartRef.current = point;
    updateMeasurement({
      ...(physicalMeasurement || fallbackPhysicalMeasurement),
      start: point,
      end: point,
    });
  };

  const continueDrawMeasurement = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      !drawingMeasurement ||
      editMode !== "measurement" ||
      !event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      return;
    }
    const start = drawingStartRef.current;
    const point = pointFromEvent(event);
    if (!start || !point) return;
    updateMeasurement({
      ...(physicalMeasurement || fallbackPhysicalMeasurement),
      start,
      end: point,
    });
  };

  const endDrawMeasurement = () => {
    if (drawingStartRef.current) setDrawingMeasurement(false);
    drawingStartRef.current = null;
  };

  const setMode = (mode: EditMode) => {
    setEditMode(mode);
    if (mode !== "measurement") setDrawingMeasurement(false);
  };

  const switchMeasurementUnit = (unit: "mm" | "in") => {
    const current = physicalMeasurement || fallbackPhysicalMeasurement;
    if (current.unit === unit) return;
    const currentMm = measurementMm(current);
    updateMeasurement({
      ...current,
      unit,
      value: unit === "in" ? currentMm / INCH_TO_MM : currentMm,
    });
  };

  const fitPendantBodyToMeasurement = () => {
    if (!physicalMeasurement) return;
    const minX = Math.min(physicalMeasurement.start.x, physicalMeasurement.end.x);
    const maxX = Math.max(physicalMeasurement.start.x, physicalMeasurement.end.x);
    const minY = Math.min(physicalMeasurement.start.y, physicalMeasurement.end.y);
    const maxY = Math.max(physicalMeasurement.start.y, physicalMeasurement.end.y);
    const center = {
      x: (physicalMeasurement.start.x + physicalMeasurement.end.x) / 2,
      y: (physicalMeasurement.start.y + physicalMeasurement.end.y) / 2,
    };
    const lineWidth = Math.max(maxX - minX, 0.08);
    const lineHeight = Math.max(maxY - minY, 0.02);
    const width = clampSize(Math.min(0.95, lineWidth * 1.18));
    const height = clampSize(Math.min(0.95, Math.max(lineHeight * 1.18, lineWidth * 1.35, 0.16)));
    updateRegion("pendantBodyBounds", {
      x: clamp01(Math.min(center.x - width / 2, 1 - width)),
      y: clamp01(Math.min(center.y - height / 2, 1 - height)),
      width,
      height,
    });
    setEditMode("pendantBodyBounds");
  };

  const warnings = useMemo(() => {
    const next: string[] = [];
    const chainDistance = Math.hypot(
      anchors.rightChain.x - anchors.leftChain.x,
      anchors.rightChain.y - anchors.leftChain.y
    );
    if (chainDistance < 0.08) {
      next.push("Chain anchors are very close together; the necklace may collapse into a narrow line.");
    }
    if (!physicalMeasurement && !value?.pendantWidthMm && !value?.pendantHeightMm) {
      next.push("No physical measurement or pendant width/height is configured; storefront sizing will use the legacy fallback.");
    }
    if (physicalMeasurement) {
      if (
        Math.hypot(
          physicalMeasurement.end.x - physicalMeasurement.start.x,
          physicalMeasurement.end.y - physicalMeasurement.start.y
        ) < 0.001
      ) {
        next.push("Measurement points A and B are almost identical.");
      }
      if (!lineIntersectsRect(physicalMeasurement, pendantBodyBounds)) {
        next.push("The physical measurement line does not touch the pendant body bounds.");
      }
      if (calculatedAssetPixelsPerMm <= 0) {
        next.push("Asset pixels/mm could not be calculated from the current measurement line.");
      }
    }
    if (!rectContainsRect(fullAssetBounds, pendantBodyBounds)) {
      next.push("Pendant body bounds are outside the full visible asset bounds.");
    }
    if (!rectContainsRect(pendantBodyBounds, photoRegion)) {
      next.push("Photo region is outside the pendant body bounds.");
    }
    if (
      anchors.leftChain.y > pendantBodyBounds.y + pendantBodyBounds.height * 0.35 ||
      anchors.rightChain.y > pendantBodyBounds.y + pendantBodyBounds.height * 0.35
    ) {
      next.push("Chain anchors look low relative to the pendant body.");
    }
    const opaque = value?.opaqueBounds || imageAnalysis?.opaqueBounds;
    if (opaque && opaque.width * opaque.height < 0.28) {
      next.push("Transparent margins are large; use the detected full asset/body bounds instead of full image size.");
    }
    if (value?.side === "default") {
      next.push("Side is set to default; choose front/back for side-specific try-on variants when possible.");
    }
    if (imageAnalysis?.error) {
      next.push(imageAnalysis.error);
    }
    return next;
  }, [
    anchors.leftChain,
    anchors.rightChain,
    calculatedAssetPixelsPerMm,
    fullAssetBounds,
    imageAnalysis?.error,
    imageAnalysis?.opaqueBounds,
    pendantBodyBounds,
    physicalMeasurement,
    photoRegion,
    value?.opaqueBounds,
    value?.pendantHeightMm,
    value?.pendantWidthMm,
    value?.side,
  ]);

  const adminPreview = useMemo(() => {
    if (!imageUrl || !resolvedImageWidthPx || !resolvedImageHeightPx) return null;
    const sampleWidth = 360;
    const sampleHeight = 440;
    const shoulderLeft = { x: 88, y: 192 };
    const shoulderRight = { x: 272, y: 192 };
    const shoulderWidth = shoulderRight.x - shoulderLeft.x;
    const neckWidth = shoulderWidth * 0.27;
    const neckCenter = { x: sampleWidth / 2, y: shoulderLeft.y - shoulderWidth * 0.24 };
    const clavicleCenter = { x: neckCenter.x, y: neckCenter.y + shoulderWidth * 0.055 };
    const sourceLeft = {
      x: anchors.leftChain.x * resolvedImageWidthPx,
      y: anchors.leftChain.y * resolvedImageHeightPx,
    };
    const sourceRight = {
      x: anchors.rightChain.x * resolvedImageWidthPx,
      y: anchors.rightChain.y * resolvedImageHeightPx,
    };
    const sourceMid = {
      x: (sourceLeft.x + sourceRight.x) / 2,
      y: (sourceLeft.y + sourceRight.y) / 2,
    };
    const sourceAngle = Math.atan2(
      sourceRight.y - sourceLeft.y,
      sourceRight.x - sourceLeft.x
    );
    const cameraPixelsPerMm = shoulderWidth / 410;
    const bodyWidthPx = pendantBodyBounds.width * resolvedImageWidthPx;
    const configuredWidthMm =
      Number(value?.pendantWidthMm) ||
      measurementMm(physicalMeasurement) ||
      54;
    const assetPixelsPerMm =
      calculatedAssetPixelsPerMm ||
      Number(value?.assetPixelsPerMm) ||
      (bodyWidthPx > 0 && configuredWidthMm > 0 ? bodyWidthPx / configuredWidthMm : 0);
    if (!assetPixelsPerMm) return null;
    const sourceChainWidth = Math.hypot(
      sourceRight.x - sourceLeft.x,
      sourceRight.y - sourceLeft.y
    );
    const sourceAxis =
      sourceChainWidth > 0
        ? {
            x: (sourceRight.x - sourceLeft.x) / sourceChainWidth,
            y: (sourceRight.y - sourceLeft.y) / sourceChainWidth,
          }
        : { x: 1, y: 0 };
    let sourceDown = { x: -sourceAxis.y, y: sourceAxis.x };
    if (sourceDown.y < 0) sourceDown = { x: -sourceDown.x, y: -sourceDown.y };
    const sourcePendantTop = {
      x: (anchors.pendantTop?.x ?? anchors.pendantCenter.x) * resolvedImageWidthPx,
      y: (anchors.pendantTop?.y ?? anchors.pendantCenter.y) * resolvedImageHeightPx,
    };
    const assetPendantTopDropPx =
      (sourcePendantTop.x - sourceMid.x) * sourceDown.x +
      (sourcePendantTop.y - sourceMid.y) * sourceDown.y;
    const assetPendantTopDropMm =
      assetPendantTopDropPx > 0 ? assetPendantTopDropPx / assetPixelsPerMm : 0;
    const requestedWearDropMm = Number(value?.wearDropMm);
    const maxWearDropCorrection =
      (value?.chainIncluded === false
        ? MAX_LOOSE_CHAIN_WEAR_DROP_CORRECTION_MM
        : MAX_INCLUDED_CHAIN_WEAR_DROP_CORRECTION_MM) * cameraPixelsPerMm;
    const wearDropCorrection =
      Number.isFinite(requestedWearDropMm) &&
      requestedWearDropMm > 0 &&
      assetPendantTopDropMm > 0
        ? Math.max(
            -maxWearDropCorrection,
            Math.min(
              maxWearDropCorrection,
              (requestedWearDropMm - assetPendantTopDropMm) * cameraPixelsPerMm
            )
          )
        : 0;
    const chainLengthInches =
      Number(value?.chainLengthInches) ||
      (Number(value?.chainLengthMm) > 0 ? Number(value?.chainLengthMm) / INCH_TO_MM : 18);
    const chainDrop =
      value?.chainIncluded === false
        ? Math.max(-0.05, Math.min(0.14, (chainLengthInches - 18) * 0.018)) *
          shoulderWidth
        : 0;
    const defaultWearLift = DEFAULT_TRY_ON_WEAR_LIFT_MM * cameraPixelsPerMm;
    const verticalOffset =
      shoulderWidth *
        Number(value?.defaultVerticalOffset ?? value?.verticalOffset ?? 0) +
      chainDrop +
      Number(value?.wearOffsetYMm || 0) * cameraPixelsPerMm +
      wearDropCorrection -
      defaultWearLift;
    const horizontalOffset =
      shoulderWidth *
        Number(value?.defaultHorizontalOffset ?? value?.horizontalOffset ?? 0) +
      Number(value?.wearOffsetXMm || 0) * cameraPixelsPerMm;
    const targetLeft = {
      x: clavicleCenter.x - neckWidth / 2 + horizontalOffset,
      y: clavicleCenter.y + verticalOffset,
    };
    const targetRight = {
      x: clavicleCenter.x + neckWidth / 2 + horizontalOffset,
      y: clavicleCenter.y + verticalOffset,
    };
    const targetMid = {
      x: (targetLeft.x + targetRight.x) / 2,
      y: (targetLeft.y + targetRight.y) / 2,
    };
    const hasPhysicalScale =
      Boolean(physicalMeasurement) ||
      Number(value?.pendantWidthMm) > 0 ||
      Number(value?.pendantHeightMm) > 0;
    const physicalScaleCorrection = clampScaleCorrection(
      Number(value?.physicalScaleCorrection ?? 1)
    );
    const scale =
      (cameraPixelsPerMm / assetPixelsPerMm) *
      (hasPhysicalScale
        ? physicalScaleCorrection
        : Number(value?.defaultScale ?? 1) * physicalScaleCorrection);
    const rotation =
      -sourceAngle +
      ((Number(value?.defaultRotationOffset ?? value?.rotationOffset ?? 0) +
        Number(value?.wearRotationDeg || 0)) *
        Math.PI) /
        180;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const a = cos * scale;
    const b = sin * scale;
    const c = -sin * scale;
    const d = cos * scale;
    const e = targetMid.x - (a * sourceMid.x + c * sourceMid.y);
    const f = targetMid.y - (b * sourceMid.x + d * sourceMid.y);
    return {
      sampleWidth,
      sampleHeight,
      shoulderLeft,
      shoulderRight,
      neckCenter,
      clavicleCenter,
      targetLeft,
      targetRight,
      transform: `matrix(${a}, ${b}, ${c}, ${d}, ${e}, ${f})`,
      scale,
      cameraPixelsPerMm,
      assetPixelsPerMm,
      physicalScaleCorrection,
      renderedWidthPx: bodyWidthPx * scale,
      assetPendantTopDropMm,
      wearDropCorrection,
      defaultWearLift,
    };
  }, [
    anchors.leftChain,
    anchors.pendantCenter,
    anchors.pendantTop,
    anchors.rightChain,
    calculatedAssetPixelsPerMm,
    imageUrl,
    pendantBodyBounds,
    physicalMeasurement,
    resolvedImageHeightPx,
    resolvedImageWidthPx,
    value?.assetPixelsPerMm,
    value?.chainIncluded,
    value?.chainLengthInches,
    value?.chainLengthMm,
    value?.defaultHorizontalOffset,
    value?.defaultRotationOffset,
    value?.defaultScale,
    value?.defaultVerticalOffset,
    value?.horizontalOffset,
    value?.pendantHeightMm,
    value?.pendantWidthMm,
    value?.physicalScaleCorrection,
    value?.rotationOffset,
    value?.verticalOffset,
    value?.wearDropMm,
    value?.wearOffsetXMm,
    value?.wearOffsetYMm,
    value?.wearRotationDeg,
  ]);

  const regionStyle = (
    bounds: VirtualTryOnBounds,
    color: string,
    borderRadius: string,
    active: boolean
  ): React.CSSProperties => ({
    position: "absolute",
    left: `${bounds.x * 100}%`,
    top: `${bounds.y * 100}%`,
    width: `${bounds.width * 100}%`,
    height: `${bounds.height * 100}%`,
    border: `${active ? 2 : 1.5}px ${active ? "solid" : "dashed"} ${color}`,
    background: active ? `${color}14` : "transparent",
    borderRadius,
    cursor: active ? "move" : "default",
    zIndex: 2,
    opacity: active ? 1 : 0.62,
    pointerEvents: active ? "auto" : "none",
  });

  const activeRegionKey: RegionKey | null =
    editMode === "fullAssetBounds" ||
    editMode === "pendantBounds" ||
    editMode === "pendantBodyBounds" ||
    editMode === "photoRegion"
      ? editMode
      : null;
  const activeRegionBounds = activeRegionKey
    ? boundsForRegion(activeRegionKey)
    : null;
  const activeRegionLabel =
    editModes.find((mode) => mode.key === activeRegionKey)?.label || "";
  const frameOverlaySrc = assetUrl(value?.frameOverlayUrl || "") || "";
  const photoShapeClip =
    photoRegion.shape === "rect"
      ? "inset(0)"
      : photoRegion.shape === "circle"
        ? "circle(50% at 50% 50%)"
        : "ellipse(50% 50% at 50% 50%)";
  const handleTestPhotoPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (testPhotoObjectUrlRef.current) {
      URL.revokeObjectURL(testPhotoObjectUrlRef.current);
    }
    const objectUrl = URL.createObjectURL(file);
    testPhotoObjectUrlRef.current = objectUrl;
    setTestPhotoUrl(objectUrl);
  };
  const updateActiveRegionNumber = (
    field: keyof VirtualTryOnBounds,
    rawValue: string
  ) => {
    if (!activeRegionKey || !activeRegionBounds) return;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    const value = parsed / 100;
    updateRegion(
      activeRegionKey,
      normalizeBounds({
        ...activeRegionBounds,
        [field]: field === "width" || field === "height" ? clampSize(value) : clamp01(value),
      })
    );
  };

  const resetLiveScaleSession = useCallback(() => {
    liveScaleSessionRef.current = {
      samples: [],
      baselineShoulderWidthPx: 0,
      baselineCameraPixelsPerMm: 0,
      baselinePhysicalScale: 0,
      smoothedShoulderWidthPx: 0,
      depthRatio: 1,
    };
  }, []);

  const stopLiveCalibration = useCallback(() => {
    if (liveAnimationRef.current !== null) {
      window.cancelAnimationFrame(liveAnimationRef.current);
      liveAnimationRef.current = null;
    }
    livePoseRef.current?.close?.();
    livePoseRef.current = null;
    liveStreamRef.current?.getTracks().forEach((track) => track.stop());
    liveStreamRef.current = null;
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
    liveAssetImageRef.current = null;
    setLiveCalibrationActive(false);
    setLiveCalibrationLoading("");
    resetLiveScaleSession();
  }, [resetLiveScaleSession]);

  const renderLiveCalibrationFrame = useCallback(
    (timestamp: number) => {
      const canvas = liveCanvasRef.current;
      const video = liveVideoRef.current;
      const pose = livePoseRef.current;
      const image = liveAssetImageRef.current;
      const config = liveValueRef.current;
      if (!canvas || !video || !pose || !image || video.readyState < 2 || !config) {
        liveAnimationRef.current = window.requestAnimationFrame(
          renderLiveCalibrationFrame
        );
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const nextWidth = Math.max(1, Math.round((rect.width || 520) * dpr));
      const nextHeight = Math.max(1, Math.round((rect.height || 640) * dpr));
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        resetLiveScaleSession();
      }
      const canvasCssRatio = Math.max(
        canvas.width / Math.max(rect.width || 1, 1),
        0.0001
      );
      const drawRect = getVideoCoverRect(canvas, video);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawMirroredCalibrationVideo(ctx, canvas, video, drawRect);

      const result = pose.detectForVideo(video, timestamp);
      const landmarks = result.landmarks?.[0];
      const leftShoulder = landmarks?.[POSE_LEFT_SHOULDER];
      const rightShoulder = landmarks?.[POSE_RIGHT_SHOULDER];
      const shoulderConfidence = Math.min(
        landmarkConfidence(leftShoulder),
        landmarkConfidence(rightShoulder)
      );
      if (!leftShoulder || !rightShoulder || shoulderConfidence < 0.45) {
        ctx.fillStyle = "rgba(15,23,42,0.72)";
        ctx.fillRect(18, 18, 420, 54);
        ctx.fillStyle = "#fff";
        ctx.font = "700 15px Arial, sans-serif";
        ctx.fillText("Position your head and shoulders in frame.", 32, 50);
        liveAnimationRef.current = window.requestAnimationFrame(
          renderLiveCalibrationFrame
        );
        return;
      }

      const shoulderA = mapCameraPointToDisplay(leftShoulder, drawRect, true);
      const shoulderB = mapCameraPointToDisplay(rightShoulder, drawRect, true);
      const shoulderLeft = shoulderA.x <= shoulderB.x ? shoulderA : shoulderB;
      const shoulderRight = shoulderA.x <= shoulderB.x ? shoulderB : shoulderA;
      const shoulderWidth = pointDistance(shoulderLeft, shoulderRight);
      const shoulderWidthCss = shoulderWidth / canvasCssRatio;
      const axis = {
        x: (shoulderRight.x - shoulderLeft.x) / Math.max(shoulderWidth, 1),
        y: (shoulderRight.y - shoulderLeft.y) / Math.max(shoulderWidth, 1),
      };
      let down = { x: -axis.y, y: axis.x };
      if (down.y < 0) down = { x: -down.x, y: -down.y };
      const shoulderMid = {
        x: (shoulderLeft.x + shoulderRight.x) / 2,
        y: (shoulderLeft.y + shoulderRight.y) / 2,
      };
      const neckCenter = {
        x: shoulderMid.x - down.x * shoulderWidth * 0.24,
        y: shoulderMid.y - down.y * shoulderWidth * 0.24,
      };
      const clavicleCenter = {
        x: neckCenter.x + down.x * shoulderWidth * 0.055,
        y: neckCenter.y + down.y * shoulderWidth * 0.055,
      };
      const anchorsForConfig = getAnchors(config);
      const measurement = normalizePhysicalMeasurement(config.physicalMeasurement);
      const assetPixelsPerMm =
        assetPixelsPerMmFor(measurement, image.naturalWidth, image.naturalHeight) ||
        Number(config.assetPixelsPerMm) ||
        0;
      const configuredMm = measurementMm(measurement);
      if (!assetPixelsPerMm || !configuredMm) {
        ctx.fillStyle = "rgba(15,23,42,0.74)";
        ctx.fillRect(18, 18, 500, 58);
        ctx.fillStyle = "#fff";
        ctx.font = "700 15px Arial, sans-serif";
        ctx.fillText("Draw A/B and enter the real distance before live calibration.", 32, 52);
        liveAnimationRef.current = window.requestAnimationFrame(
          renderLiveCalibrationFrame
        );
        return;
      }

      const correction = liveCorrectionRef.current;
      const cameraCssPixelsPerMm = shoulderWidthCss / 410;
      const session = updateLiveScaleSession(liveScaleSessionRef.current, {
        shoulderWidthPx: shoulderWidthCss,
        cameraPixelsPerMm: cameraCssPixelsPerMm,
        assetPixelsPerMm,
        correction,
      });
      liveScaleSessionRef.current = session;
      const ready = session.baselinePhysicalScale > 0;
      const rawImageScaleCss = cameraCssPixelsPerMm / assetPixelsPerMm;
      const correctedBaseScaleCss = ready
        ? session.baselinePhysicalScale
        : rawImageScaleCss * correction;
      const finalScaleCss = ready
        ? session.baselinePhysicalScale * session.depthRatio
        : correctedBaseScaleCss;
      const finalScale = finalScaleCss * canvasCssRatio;
      const currentCameraCssPixelsPerMm = ready
        ? session.baselineCameraPixelsPerMm * session.depthRatio
        : cameraCssPixelsPerMm;
      const currentCameraPixelsPerMm =
        currentCameraCssPixelsPerMm * canvasCssRatio;

      const sourceLeft = {
        x: anchorsForConfig.leftChain.x * image.naturalWidth,
        y: anchorsForConfig.leftChain.y * image.naturalHeight,
      };
      const sourceRight = {
        x: anchorsForConfig.rightChain.x * image.naturalWidth,
        y: anchorsForConfig.rightChain.y * image.naturalHeight,
      };
      const sourceMid = {
        x: (sourceLeft.x + sourceRight.x) / 2,
        y: (sourceLeft.y + sourceRight.y) / 2,
      };
      const sourceAngle = Math.atan2(
        sourceRight.y - sourceLeft.y,
        sourceRight.x - sourceLeft.x
      );
      const targetMid = {
        x:
          clavicleCenter.x +
          axis.x * shoulderWidth * Number(config.defaultHorizontalOffset ?? config.horizontalOffset ?? 0) +
          axis.x * Number(config.wearOffsetXMm || 0) * currentCameraPixelsPerMm,
        y:
          clavicleCenter.y -
          down.y * DEFAULT_TRY_ON_WEAR_LIFT_MM * currentCameraPixelsPerMm +
          down.y * shoulderWidth * Number(config.defaultVerticalOffset ?? config.verticalOffset ?? 0) +
          down.y * Number(config.wearOffsetYMm || 0) * currentCameraPixelsPerMm,
      };
      const rotation =
        Math.atan2(axis.y, axis.x) -
        sourceAngle +
        ((Number(config.defaultRotationOffset ?? config.rotationOffset ?? 0) +
          Number(config.wearRotationDeg || 0)) *
          Math.PI) /
          180;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const a = cos * finalScale;
      const b = sin * finalScale;
      const c = -sin * finalScale;
      const d = cos * finalScale;
      const e = targetMid.x - (a * sourceMid.x + c * sourceMid.y);
      const f = targetMid.y - (b * sourceMid.x + d * sourceMid.y);
      const transformPoint = (point: VirtualTryOnPoint) => ({
        x: a * point.x + c * point.y + e,
        y: b * point.x + d * point.y + f,
      });

      ctx.save();
      ctx.setTransform(a, b, c, d, e, f);
      ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
      ctx.restore();

      if (measurement) {
        const start = transformPoint({
          x: measurement.start.x * image.naturalWidth,
          y: measurement.start.y * image.naturalHeight,
        });
        const end = transformPoint({
          x: measurement.end.x * image.naturalWidth,
          y: measurement.end.y * image.naturalHeight,
        });
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.strokeStyle = "#22d3ee";
        ctx.lineWidth = 4;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        ctx.fillStyle = "#0f172a";
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 3;
        ctx.font = "800 14px Arial, sans-serif";
        ctx.textAlign = "center";
        const labelX = (start.x + end.x) / 2;
        const labelY = (start.y + end.y) / 2 - 10;
        ctx.strokeText(`${configuredMm.toFixed(0)} mm`, labelX, labelY);
        ctx.fillText(`${configuredMm.toFixed(0)} mm`, labelX, labelY);
        [
          { point: start, label: "A" },
          { point: end, label: "B" },
        ].forEach(({ point, label }) => {
          ctx.fillStyle = "#22d3ee";
          ctx.beginPath();
          ctx.arc(point.x, point.y, 7, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#0f172a";
          ctx.font = "800 11px Arial, sans-serif";
          ctx.fillText(label, point.x, point.y + 4);
        });
        ctx.restore();
      }

      if (timestamp - lastLiveDebugAtRef.current > 220) {
        lastLiveDebugAtRef.current = timestamp;
        const assetMeasurementPx = measurementLinePx(
          measurement,
          image.naturalWidth,
          image.naturalHeight
        );
        setLiveDebug({
          configuredMm,
          assetMeasurementPx,
          assetPixelsPerMm,
          rawCameraPixelsPerMm: cameraCssPixelsPerMm,
          rawImageScale: rawImageScaleCss,
          physicalScaleCorrection: correction,
          correctedBaseScale: correctedBaseScaleCss,
          baselineShoulderWidthPx: session.baselineShoulderWidthPx,
          currentShoulderWidthPx: shoulderWidthCss,
          depthRatio: session.depthRatio || 1,
          finalRenderedScale: finalScaleCss,
          renderedMeasurementPx: assetMeasurementPx * finalScaleCss,
        });
      }

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(shoulderLeft.x, shoulderLeft.y);
      ctx.lineTo(shoulderRight.x, shoulderRight.y);
      ctx.stroke();
      ctx.fillStyle = "rgba(15,23,42,0.72)";
      ctx.fillRect(16, 16, 370, 78);
      ctx.fillStyle = "#fff";
      ctx.font = "700 13px Arial, sans-serif";
      ctx.fillText("Live Size Calibration", 30, 40);
      ctx.font = "12px Arial, sans-serif";
      ctx.fillText(
        ready
          ? `Depth ${session.depthRatio.toFixed(3)}x | correction ${correction.toFixed(3)}x`
          : `Collecting baseline ${session.samples.length}/24 | correction ${correction.toFixed(3)}x`,
        30,
        62
      );
      ctx.fillText("Hold ruler at chest depth and match A-B.", 30, 82);
      ctx.restore();

      liveAnimationRef.current = window.requestAnimationFrame(
        renderLiveCalibrationFrame
      );
    },
    [resetLiveScaleSession]
  );

  const startLiveCalibration = useCallback(async () => {
    if (!imageUrl || !physicalMeasurement) {
      setLiveCalibrationError("Draw A/B and enter its real distance first.");
      return;
    }
    stopLiveCalibration();
    setLiveCalibrationError("");
    setLiveCalibrationLoading("Starting webcam...");
    resetLiveScaleSession();
    setLiveScaleCorrection(
      clampScaleCorrection(Number(value?.physicalScaleCorrection ?? 1))
    );
    setLiveCalibrationActive(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      liveStreamRef.current = stream;
      if (!liveVideoRef.current) throw new Error("Calibration video is not ready.");
      liveVideoRef.current.srcObject = stream;
      await liveVideoRef.current.play();
      setLiveCalibrationLoading("Loading body tracker...");
      livePoseRef.current = await loadPoseLandmarker();
      liveAssetImageRef.current = await loadCalibrationImage(imageUrl);
      setLiveCalibrationLoading("");
      liveAnimationRef.current = window.requestAnimationFrame(
        renderLiveCalibrationFrame
      );
    } catch (error: any) {
      setLiveCalibrationError(
        error?.message || "Unable to start live size calibration."
      );
      stopLiveCalibration();
    }
  }, [
    imageUrl,
    physicalMeasurement,
    renderLiveCalibrationFrame,
    resetLiveScaleSession,
    stopLiveCalibration,
    value?.physicalScaleCorrection,
  ]);

  const adjustLiveScaleCorrection = useCallback((factor: number) => {
    setLiveScaleCorrection((current) => {
      const next = clampScaleCorrection(current * factor);
      liveCorrectionRef.current = next;
      return next;
    });
  }, []);

  const saveLiveScaleCalibration = useCallback(() => {
    const nextCorrection = clampScaleCorrection(liveScaleCorrection);
    onChange({
      ...nextBase(),
      physicalScaleCorrection: nextCorrection,
      physicalScaleCalibratedAt: new Date().toISOString(),
      physicalScaleCalibrationNote: "Measured with live Admin webcam calibration",
    });
  }, [liveScaleCorrection, nextBase, onChange]);

  useEffect(() => {
    return () => stopLiveCalibration();
  }, [stopLiveCalibration]);

  if (!imageUrl) {
    return (
      <div style={{ color: "#64748b", fontSize: 12 }}>
        Upload a transparent necklace PNG to calibrate try-on anchors and regions.
      </div>
    );
  }

  const outlineBtn: React.CSSProperties = {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#334155",
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 44,
    minWidth: 44,
    touchAction: "manipulation",
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
          Select what you want to move
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {editModes.map((mode) => {
            const active = editMode === mode.key;
            return (
              <button
                key={mode.key}
                type="button"
                title={mode.description}
                onClick={() => setMode(mode.key)}
                style={{
                  border: `1px solid ${active ? "#111827" : "#cbd5e1"}`,
                  borderRadius: 8,
                  background: active ? "#111827" : "#fff",
                  color: active ? "#fff" : "#334155",
                  padding: "7px 9px",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                {mode.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={stageRef}
        onPointerDown={beginDrawMeasurement}
        onPointerMove={continueDrawMeasurement}
        onPointerUp={endDrawMeasurement}
        style={{
          position: "relative",
          maxWidth: 420,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
          background:
            "linear-gradient(45deg,#f8fafc 25%,transparent 25%),linear-gradient(-45deg,#f8fafc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f8fafc 75%),linear-gradient(-45deg,transparent 75%,#f8fafc 75%)",
          backgroundSize: "18px 18px",
          backgroundPosition: "0 0,0 9px,9px -9px,-9px 0",
          touchAction: "none",
          cursor:
            editMode === "measurement" && drawingMeasurement ? "crosshair" : undefined,
        }}
      >
        <img
          src={imageUrl}
          alt="Virtual try-on necklace calibration"
          style={{ display: "block", width: "100%", height: "auto", userSelect: "none" }}
          draggable={false}
        />

        {physicalMeasurement ? (
          <svg
            aria-hidden="true"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: editMode === "measurement" ? "auto" : "none",
              zIndex: 4,
            }}
          >
            <line
              x1={physicalMeasurement.start.x * 100}
              y1={physicalMeasurement.start.y * 100}
              x2={physicalMeasurement.end.x * 100}
              y2={physicalMeasurement.end.y * 100}
              stroke="transparent"
              strokeWidth={10}
              vectorEffect="non-scaling-stroke"
              pointerEvents="stroke"
              style={{ cursor: "move", pointerEvents: "stroke" }}
              onPointerDown={(event) =>
                beginDrag({ type: "measurementLine" }, event)
              }
              onPointerMove={(event) =>
                continueDrag({ type: "measurementLine" }, event)
              }
              onPointerUp={endDrag}
            />
            <line
              x1={physicalMeasurement.start.x * 100}
              y1={physicalMeasurement.start.y * 100}
              x2={physicalMeasurement.end.x * 100}
              y2={physicalMeasurement.end.y * 100}
              stroke="#2563eb"
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          </svg>
        ) : null}

        {physicalMeasurement ? (
          <>
            {(["start", "end"] as const).map((key) => {
              const point = physicalMeasurement[key];
              return (
                <button
                  key={`measurement-${key}`}
                  type="button"
                  title={`Physical measurement point ${key === "start" ? "A" : "B"}`}
                  onPointerDown={(event) =>
                    beginDrag({ type: "measurement", key }, event)
                  }
                  onPointerMove={(event) =>
                    continueDrag({ type: "measurement", key }, event)
                  }
                  style={{
                    position: "absolute",
                    zIndex: 8,
                    left: `${point.x * 100}%`,
                    top: `${point.y * 100}%`,
                    transform: "translate(-50%, -50%)",
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    border: "2px solid #1d4ed8",
                    background: "rgba(239,246,255,0.22)",
                    color: "#1e3a8a",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor:
                      editMode === "points" || editMode === "measurement"
                        ? "grab"
                        : "default",
                    boxShadow: "0 8px 20px rgba(30,64,175,.25)",
                    backdropFilter: "blur(1px)",
                    pointerEvents:
                      editMode === "points" || editMode === "measurement"
                        ? "auto"
                        : "none",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: 3,
                      top: 1,
                      lineHeight: 1,
                    }}
                  >
                    {key === "start" ? "A" : "B"}
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 7,
                      height: 7,
                      borderRadius: 999,
                      background: "#1d4ed8",
                      border: "1px solid #fff",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </button>
              );
            })}
            <div
              style={{
                position: "absolute",
                zIndex: 7,
                left: `${((physicalMeasurement.start.x + physicalMeasurement.end.x) / 2) * 100}%`,
                top: `${((physicalMeasurement.start.y + physicalMeasurement.end.y) / 2) * 100}%`,
                transform: "translate(-50%, calc(-100% - 10px))",
                borderRadius: 8,
                background: "#1d4ed8",
                color: "#fff",
                padding: "4px 7px",
                fontSize: 11,
                fontWeight: 800,
                boxShadow: "0 8px 20px rgba(30,64,175,.24)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              {displayMeasurement(physicalMeasurement)}
            </div>
          </>
        ) : null}

        <div
          title="Full visible asset bounds"
          onPointerDown={(event) =>
            beginDrag({ type: "region", key: "fullAssetBounds", mode: "move" }, event)
          }
          onPointerMove={(event) =>
            continueDrag({ type: "region", key: "fullAssetBounds", mode: "move" }, event)
          }
          onPointerUp={endDrag}
          style={regionStyle(
            fullAssetBounds,
            "#6366f1",
            "10px",
            editMode === "fullAssetBounds"
          )}
        >
          <span
            style={{
              position: "absolute",
              left: 4,
              top: 4,
              borderRadius: 6,
              background: "#6366f1",
              color: "#fff",
              padding: "2px 5px",
              fontSize: 10,
              fontWeight: 800,
              pointerEvents: "none",
            }}
          >
            Full asset
          </span>
          <span
            onPointerDown={(event) =>
              beginDrag(
                { type: "region", key: "fullAssetBounds", mode: "resize" },
                event
              )
            }
            onPointerMove={(event) =>
              continueDrag(
                { type: "region", key: "fullAssetBounds", mode: "resize" },
                event
              )
            }
            onPointerUp={endDrag}
            style={{
              position: "absolute",
              right: -7,
              bottom: -7,
              width: 16,
              height: 16,
              borderRadius: 999,
              background: "#6366f1",
              border: "2px solid #fff",
              cursor: "nwse-resize",
              pointerEvents: editMode === "fullAssetBounds" ? "auto" : "none",
            }}
          />
        </div>

        <div
          title="Pendant outside bounds"
          onPointerDown={(event) =>
            beginDrag({ type: "region", key: "pendantBounds", mode: "move" }, event)
          }
          onPointerMove={(event) =>
            continueDrag({ type: "region", key: "pendantBounds", mode: "move" }, event)
          }
          onPointerUp={endDrag}
          style={regionStyle(
            pendantBounds,
            "#f59e0b",
            "10px",
            editMode === "pendantBounds"
          )}
        >
          <span
            style={{
              position: "absolute",
              left: 4,
              top: 4,
              borderRadius: 6,
              background: "#f59e0b",
              color: "#111827",
              padding: "2px 5px",
              fontSize: 10,
              fontWeight: 800,
              pointerEvents: "none",
            }}
          >
            Pendant bounds
          </span>
          <span
            onPointerDown={(event) =>
              beginDrag(
                { type: "region", key: "pendantBounds", mode: "resize" },
                event
              )
            }
            onPointerMove={(event) =>
              continueDrag(
                { type: "region", key: "pendantBounds", mode: "resize" },
                event
              )
            }
            onPointerUp={endDrag}
            style={{
              position: "absolute",
              right: -7,
              bottom: -7,
              width: 16,
              height: 16,
              borderRadius: 999,
              background: "#f59e0b",
              border: "2px solid #fff",
              cursor: "nwse-resize",
              pointerEvents: editMode === "pendantBounds" ? "auto" : "none",
            }}
          />
        </div>

        <div
          title="Physical pendant body bounds"
          onPointerDown={(event) =>
            beginDrag({ type: "region", key: "pendantBodyBounds", mode: "move" }, event)
          }
          onPointerMove={(event) =>
            continueDrag({ type: "region", key: "pendantBodyBounds", mode: "move" }, event)
          }
          onPointerUp={endDrag}
          style={regionStyle(
            pendantBodyBounds,
            "#ef4444",
            "10px",
            editMode === "pendantBodyBounds"
          )}
        >
          <span
            style={{
              position: "absolute",
              right: 4,
              top: 4,
              borderRadius: 6,
              background: "#ef4444",
              color: "#fff",
              padding: "2px 5px",
              fontSize: 10,
              fontWeight: 800,
              pointerEvents: "none",
            }}
          >
            Body
          </span>
          <span
            onPointerDown={(event) =>
              beginDrag(
                { type: "region", key: "pendantBodyBounds", mode: "resize" },
                event
              )
            }
            onPointerMove={(event) =>
              continueDrag(
                { type: "region", key: "pendantBodyBounds", mode: "resize" },
                event
              )
            }
            onPointerUp={endDrag}
            style={{
              position: "absolute",
              right: -7,
              bottom: -7,
              width: 16,
              height: 16,
              borderRadius: 999,
              background: "#ef4444",
              border: "2px solid #fff",
              cursor: "nwse-resize",
              pointerEvents: editMode === "pendantBodyBounds" ? "auto" : "none",
            }}
          />
        </div>

        <div
          title="Customer photo area"
          onPointerDown={(event) =>
            beginDrag({ type: "region", key: "photoRegion", mode: "move" }, event)
          }
          onPointerMove={(event) =>
            continueDrag({ type: "region", key: "photoRegion", mode: "move" }, event)
          }
          onPointerUp={endDrag}
          style={{
            ...regionStyle(
              photoRegion,
              "#22c55e",
              photoRegion.shape === "rect" ? "6px" : "999px",
              editMode === "photoRegion"
            ),
            transform: `rotate(${Number(photoRegion.rotation || 0)}deg)`,
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 4,
              top: 4,
              borderRadius: 6,
              background: "#22c55e",
              color: "#052e16",
              padding: "2px 5px",
              fontSize: 10,
              fontWeight: 800,
              pointerEvents: "none",
            }}
          >
            Photo area
          </span>
          <span
            onPointerDown={(event) =>
              beginDrag({ type: "region", key: "photoRegion", mode: "resize" }, event)
            }
            onPointerMove={(event) =>
              continueDrag({ type: "region", key: "photoRegion", mode: "resize" }, event)
            }
            onPointerUp={endDrag}
            style={{
              position: "absolute",
              right: -7,
              bottom: -7,
              width: 16,
              height: 16,
              borderRadius: 999,
              background: "#22c55e",
              border: "2px solid #fff",
              cursor: "nwse-resize",
              pointerEvents: editMode === "photoRegion" ? "auto" : "none",
            }}
          />
        </div>

        {anchorMeta.map((anchor) => {
          const point = anchors[anchor.key] || fallbackAnchors[anchor.key];
          return (
            <button
              key={anchor.key}
              type="button"
              title={anchor.name}
              onPointerDown={(event) =>
                beginDrag({ type: "anchor", key: anchor.key }, event)
              }
              onPointerMove={(event) =>
                continueDrag({ type: "anchor", key: anchor.key }, event)
              }
              style={{
                position: "absolute",
                zIndex: 9,
                left: `${point.x * 100}%`,
                top: `${point.y * 100}%`,
                transform: "translate(-50%, -50%)",
                width: 34,
                height: 34,
                borderRadius: 8,
                border: "2px solid #111827",
                background: "rgba(255,255,255,0.2)",
                color: "#111827",
                fontSize: 12,
                fontWeight: 800,
                cursor: editMode === "points" ? "grab" : "default",
                boxShadow: "0 8px 20px rgba(15,23,42,.22)",
                backdropFilter: "blur(1px)",
                pointerEvents: editMode === "points" ? "auto" : "none",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 3,
                  top: 1,
                  lineHeight: 1,
                }}
              >
                {anchor.label}
              </span>
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: "#111827",
                  border: "1px solid #fff",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </button>
          );
        })}
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background: "#fff",
          padding: 10,
          display: "grid",
          gap: 10,
          fontSize: 12,
          color: "#334155",
        }}
      >
        <div style={{ fontWeight: 800, color: "#0f172a" }}>
          Geometry Guide
        </div>
        <div style={{ display: "grid", gap: 5 }}>
          <div><strong>Full PNG:</strong> all visible jewelry pixels in the transparent PNG, including chain.</div>
          <div><strong>Legacy bounds:</strong> old fallback pendant box, kept only for compatibility.</div>
          <div><strong>Pendant body:</strong> the real pendant body, excluding chain and transparent padding.</div>
          <div><strong>Photo area:</strong> where the customer photo or engraving is printed inside the pendant.</div>
        </div>
        {activeRegionKey && activeRegionBounds ? (
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              paddingTop: 10,
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a" }}>
              Selected: {activeRegionLabel}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(["x", "y", "width", "height"] as const).map((field) => (
                <label
                  key={field}
                  style={{ display: "grid", gap: 4, minWidth: 86 }}
                >
                  {field === "x"
                    ? "X %"
                    : field === "y"
                      ? "Y %"
                      : field === "width"
                        ? "Width %"
                        : "Height %"}
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={(activeRegionBounds[field] * 100).toFixed(1)}
                    onChange={(event) =>
                      updateActiveRegionNumber(field, event.target.value)
                    }
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      padding: "7px 9px",
                      width: 96,
                    }}
                  />
                </label>
              ))}
            </div>
            {physicalMeasurement ? (
              <button
                type="button"
                onClick={fitPendantBodyToMeasurement}
                style={{
                  border: "1px solid #ef4444",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#b91c1c",
                  padding: "7px 10px",
                  fontSize: 12,
                  fontWeight: 800,
                  justifySelf: "start",
                }}
              >
                Fit Pendant Body Around A/B Line
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          background: "#f8fafc",
          padding: 10,
          display: "grid",
          gap: 6,
          fontSize: 12,
          color: "#334155",
        }}
      >
        <div style={{ fontWeight: 800, color: "#0f172a" }}>Detected Asset Geometry</div>
        <div>
          Image:{" "}
          {resolvedImageWidthPx && resolvedImageHeightPx
            ? `${resolvedImageWidthPx} x ${resolvedImageHeightPx}px`
            : "not inspected yet"}
        </div>
        <div>
          Opaque bounds:{" "}
          {value?.opaqueBounds || imageAnalysis?.opaqueBounds
            ? `${((value?.opaqueBounds || imageAnalysis?.opaqueBounds)!.x * 100).toFixed(1)}%, ${((value?.opaqueBounds || imageAnalysis?.opaqueBounds)!.y * 100).toFixed(1)}%, ${((value?.opaqueBounds || imageAnalysis?.opaqueBounds)!.width * 100).toFixed(1)}% x ${((value?.opaqueBounds || imageAnalysis?.opaqueBounds)!.height * 100).toFixed(1)}%`
            : "not available"}
        </div>
        <div>
          Full/body bounds: {(fullAssetBounds.width * 100).toFixed(1)}% x{" "}
          {(fullAssetBounds.height * 100).toFixed(1)}% /{" "}
          {(pendantBodyBounds.width * 100).toFixed(1)}% x{" "}
          {(pendantBodyBounds.height * 100).toFixed(1)}%
        </div>
        {warnings.length ? (
          <div
            style={{
              border: "1px solid #fde68a",
              borderRadius: 10,
              background: "#fffbeb",
              color: "#92400e",
              padding: 8,
              display: "grid",
              gap: 4,
            }}
          >
            {warnings.map((warning) => (
              <div key={warning}>{warning}</div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#166534", fontWeight: 700 }}>
            No try-on geometry warnings.
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid #dbeafe",
          borderRadius: 12,
          background: "#eff6ff",
          padding: 10,
          display: "grid",
          gap: 10,
          fontSize: 12,
          color: "#334155",
        }}
      >
        <div>
          <div style={{ fontWeight: 800, color: "#1e3a8a" }}>
            Photo Composition
          </div>
          <div>
            Preview how the try-on PNG, masked customer photo, and optional frame overlay combine.
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "end" }}>
          <label style={{ display: "grid", gap: 4 }}>
            Composition
            <select
              value={value?.photoCompositionMode || "replace-region"}
              onChange={(event) =>
                onChange({
                  ...nextBase(),
                  photoCompositionMode:
                    event.target.value === "overlay"
                      ? "overlay"
                      : "replace-region",
                })
              }
              style={{ border: "1px solid #bfdbfe", borderRadius: 8, padding: "7px 9px" }}
            >
              <option value="replace-region">Replace placeholder</option>
              <option value="overlay">Overlay on top</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 7, paddingBottom: 8 }}>
            <input
              type="checkbox"
              checked={value?.photoClipping !== false}
              onChange={(event) =>
                onChange({
                  ...nextBase(),
                  photoClipping: event.target.checked,
                })
              }
            />
            Clip to photo shape
          </label>
          <label style={{ display: "grid", gap: 4, minWidth: 220, flex: "1 1 220px" }}>
            Optional frame overlay URL
            <input
              value={value?.frameOverlayUrl || ""}
              onChange={(event) =>
                onChange({
                  ...nextBase(),
                  frameOverlayUrl: event.target.value.trim() || undefined,
                })
              }
              placeholder="/uploads/products/frame-overlay.png"
              style={{ border: "1px solid #bfdbfe", borderRadius: 8, padding: "7px 9px" }}
            />
          </label>
          <label
            style={{
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              background: "#fff",
              color: "#1d4ed8",
              padding: "7px 10px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Test Photo
            <input
              type="file"
              accept="image/*"
              onChange={handleTestPhotoPick}
              style={{ display: "none" }}
            />
          </label>
          <button
            type="button"
            onClick={() => setTestPhotoUrl(SAMPLE_TEST_PHOTO_URL)}
            style={{
              border: "1px solid #bfdbfe",
              borderRadius: 8,
              background: "#fff",
              color: "#1d4ed8",
              padding: "7px 10px",
              fontWeight: 800,
            }}
          >
            Use Sample
          </button>
        </div>
        <div
          style={{
            position: "relative",
            width: 260,
            maxWidth: "100%",
            aspectRatio:
              resolvedImageWidthPx && resolvedImageHeightPx
                ? `${resolvedImageWidthPx} / ${resolvedImageHeightPx}`
                : "1 / 1",
            overflow: "hidden",
            borderRadius: 10,
            border: "1px solid #bfdbfe",
            background:
              "linear-gradient(45deg,#fff 25%,transparent 25%),linear-gradient(-45deg,#fff 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#fff 75%),linear-gradient(-45deg,transparent 75%,#fff 75%)",
            backgroundSize: "18px 18px",
            backgroundPosition: "0 0,0 9px,9px -9px,-9px 0",
          }}
        >
          <img
            src={imageUrl}
            alt=""
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
          {testPhotoUrl ? (
            <div
              style={{
                position: "absolute",
                left: `${photoRegion.x * 100}%`,
                top: `${photoRegion.y * 100}%`,
                width: `${photoRegion.width * 100}%`,
                height: `${photoRegion.height * 100}%`,
                overflow: value?.photoClipping === false ? "visible" : "hidden",
                clipPath: value?.photoClipping === false ? undefined : photoShapeClip,
                transform: `rotate(${Number(photoRegion.rotation || 0)}deg)`,
                transformOrigin: "center center",
              }}
            >
              <img
                src={testPhotoUrl}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "108%",
                  height: "108%",
                  objectFit: "cover",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                }}
              />
            </div>
          ) : null}
          {frameOverlaySrc ? (
            <img
              src={frameOverlaySrc}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                pointerEvents: "none",
              }}
            />
          ) : null}
        </div>
      </div>

      {adminPreview ? (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
            padding: 10,
            display: "grid",
            gap: 8,
            fontSize: 12,
            color: "#334155",
          }}
        >
          <div style={{ fontWeight: 800, color: "#0f172a" }}>
            Try-On Test Preview
          </div>
          <div
            style={{
              position: "relative",
              width: adminPreview.sampleWidth,
              maxWidth: "100%",
              height: adminPreview.sampleHeight,
              overflow: "hidden",
              borderRadius: 12,
              background:
                "linear-gradient(#f8fafc,#eef2ff 48%,#e2e8f0 48%,#f8fafc)",
              border: "1px solid #e2e8f0",
            }}
          >
            <img
              src={imageUrl}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: resolvedImageWidthPx,
                height: resolvedImageHeightPx,
                transform: adminPreview.transform,
                transformOrigin: "0 0",
                pointerEvents: "none",
              }}
            />
            <svg
              aria-hidden="true"
              viewBox={`0 0 ${adminPreview.sampleWidth} ${adminPreview.sampleHeight}`}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <line
                x1={adminPreview.shoulderLeft.x}
                y1={adminPreview.shoulderLeft.y}
                x2={adminPreview.shoulderRight.x}
                y2={adminPreview.shoulderRight.y}
                stroke="#22c55e"
                strokeWidth={2}
              />
              <line
                x1={adminPreview.targetLeft.x}
                y1={adminPreview.targetLeft.y}
                x2={adminPreview.targetRight.x}
                y2={adminPreview.targetRight.y}
                stroke="#2563eb"
                strokeWidth={2}
              />
              {[
                adminPreview.shoulderLeft,
                adminPreview.shoulderRight,
                adminPreview.neckCenter,
                adminPreview.clavicleCenter,
                adminPreview.targetLeft,
                adminPreview.targetRight,
              ].map((point, index) => (
                <circle
                  key={`${point.x}-${point.y}-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={index < 2 ? 4 : 5}
                  fill={index < 2 ? "#22c55e" : "#2563eb"}
                />
              ))}
            </svg>
          </div>
          <div>
            Scale {adminPreview.scale.toFixed(3)} | camera{" "}
            {adminPreview.cameraPixelsPerMm.toFixed(3)} px/mm | asset{" "}
            {adminPreview.assetPixelsPerMm.toFixed(3)} px/mm | rendered body{" "}
            {adminPreview.renderedWidthPx.toFixed(1)} px
            {adminPreview.assetPendantTopDropMm > 0
              ? ` | asset drop ${adminPreview.assetPendantTopDropMm.toFixed(1)} mm`
              : ""}
            {adminPreview.wearDropCorrection
              ? ` | wear correction ${adminPreview.wearDropCorrection.toFixed(1)} px`
              : ""}
            {adminPreview.defaultWearLift
              ? ` | default lift ${adminPreview.defaultWearLift.toFixed(1)} px`
              : ""}
          </div>
        </div>
      ) : null}

      <div
        style={{
          border: "1px solid #dbeafe",
          borderRadius: 12,
          background: "#eff6ff",
          padding: 10,
          display: "grid",
          gap: 9,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#1e3a8a" }}>
              Real-World Scale Reference
            </div>
            <div style={{ fontSize: 12, color: "#334155" }}>
              Place A and B on any two points whose real-world distance you know.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!physicalMeasurement) updateMeasurement(fallbackPhysicalMeasurement);
              setEditMode("measurement");
              setDrawingMeasurement(true);
            }}
            style={{
              border: "1px solid #2563eb",
              borderRadius: 8,
              background: drawingMeasurement ? "#1d4ed8" : "#fff",
              color: drawingMeasurement ? "#fff" : "#1d4ed8",
              padding: "7px 10px",
              fontSize: 12,
              fontWeight: 800,
              whiteSpace: "nowrap",
            }}
          >
            Draw A/B Reference
          </button>
        </div>

        {physicalMeasurement ? (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
              <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#334155" }}>
                A <span aria-hidden="true">↔</span> B real distance
                <input
                  type="number"
                  min={0}
                  step={physicalMeasurement.unit === "in" ? 0.01 : 0.1}
                  value={physicalMeasurement.value}
                  onChange={(event) =>
                    updateMeasurement({
                      ...physicalMeasurement,
                      value: Number(event.target.value),
                    })
                  }
                  style={{
                    border: "1px solid #bfdbfe",
                    borderRadius: 8,
                    padding: "7px 9px",
                    width: 120,
                  }}
                />
              </label>
              <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#334155" }}>
                Unit
                <select
                  value={physicalMeasurement.unit}
                  onChange={(event) =>
                    switchMeasurementUnit(event.target.value === "in" ? "in" : "mm")
                  }
                  style={{
                    border: "1px solid #bfdbfe",
                    borderRadius: 8,
                    padding: "7px 9px",
                    width: 120,
                  }}
                >
                  <option value="mm">mm</option>
                  <option value="in">inches</option>
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  updateMeasurement(undefined);
                  setDrawingMeasurement(false);
                }}
                style={{
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  background: "#fff",
                  color: "#b91c1c",
                  padding: "7px 10px",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                Delete measurement
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#1e3a8a", fontWeight: 700 }}>
              Asset distance:{" "}
              {measurementLinePx(
                physicalMeasurement,
                resolvedImageWidthPx,
                resolvedImageHeightPx
              ).toFixed(1)}{" "}
              px
              {calculatedAssetPixelsPerMm > 0
                ? ` | Scale: ${calculatedAssetPixelsPerMm.toFixed(3)} asset px/mm`
                : ""}
            </div>
            <div style={{ fontSize: 12, color: "#334155" }}>
              Configured real distance: {measurementMm(physicalMeasurement).toFixed(2)} mm
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Draw A and B on any known physical span before enabling actual-size try-on.
          </div>
        )}
      </div>

      <div
        tabIndex={0}
        onKeyDown={(event) => {
          const tag = (event.target as HTMLElement).tagName;
          if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
          if (event.key === "ArrowUp") {
            event.preventDefault();
            adjustLiveScaleCorrection(event.shiftKey ? 1.05 : 1.01);
          } else if (event.key === "ArrowDown") {
            event.preventDefault();
            adjustLiveScaleCorrection(event.shiftKey ? 1 / 1.05 : 1 / 1.01);
          }
        }}
        style={{
          border: "1px solid #fde68a",
          borderRadius: 12,
          background: "#fffbeb",
          padding: 12,
          display: "grid",
          gap: 10,
          outline: "none",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#92400e" }}>
              Live Size Calibration
            </div>
            <div style={{ fontSize: 12, color: "#78350f" }}>
              Hold a ruler at the same depth as the pendant and adjust until A-B
              matches the configured real-world distance.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {liveCalibrationActive ? (
              <button type="button" style={outlineBtn} onClick={stopLiveCalibration}>
                Exit Calibration
              </button>
            ) : (
              <button
                type="button"
                style={{
                  ...outlineBtn,
                  border: "1px solid #f59e0b",
                  color: "#92400e",
                }}
                onClick={startLiveCalibration}
                disabled={!physicalMeasurement}
              >
                Start Live Calibration
              </button>
            )}
            <button
              type="button"
              style={{
                ...outlineBtn,
                border: "1px solid #16a34a",
                color: "#166534",
              }}
              onClick={saveLiveScaleCalibration}
              disabled={!physicalMeasurement}
            >
              Save Calibration
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#78350f", fontWeight: 800 }}>
            Configured reference:{" "}
            {physicalMeasurement
              ? `A ↔ B = ${measurementMm(physicalMeasurement).toFixed(2)} mm`
              : "Draw A/B first"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <button type="button" style={outlineBtn} onClick={() => adjustLiveScaleCorrection(1 / 1.05)}>
              -5%
            </button>
            <button type="button" style={outlineBtn} onClick={() => adjustLiveScaleCorrection(1 / 1.01)}>
              -1%
            </button>
            <div
              style={{
                minWidth: 92,
                textAlign: "center",
                fontSize: 18,
                fontWeight: 900,
                color: "#111827",
              }}
            >
              {liveScaleCorrection.toFixed(3)}x
            </div>
            <button type="button" style={outlineBtn} onClick={() => adjustLiveScaleCorrection(1.01)}>
              +1%
            </button>
            <button type="button" style={outlineBtn} onClick={() => adjustLiveScaleCorrection(1.05)}>
              +5%
            </button>
            <button
              type="button"
              style={outlineBtn}
              onClick={() => {
                setLiveScaleCorrection(1);
                liveCorrectionRef.current = 1;
              }}
            >
              Reset to 1.000x
            </button>
          </div>
          <input
            type="range"
            min={0.25}
            max={4}
            step={0.001}
            value={liveScaleCorrection}
            onChange={(event) => {
              const next = clampScaleCorrection(Number(event.target.value));
              liveCorrectionRef.current = next;
              setLiveScaleCorrection(next);
            }}
          />
        </div>

        <video ref={liveVideoRef} muted playsInline autoPlay style={{ display: "none" }} />
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 520,
            aspectRatio: "4 / 5",
            borderRadius: 12,
            overflow: "hidden",
            background: "#111827",
            border: "1px solid #f59e0b",
          }}
        >
          <canvas
            ref={liveCanvasRef}
            style={{ width: "100%", height: "100%", display: "block" }}
          />
          {!liveCalibrationActive ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                color: "#f8fafc",
                fontSize: 13,
                textAlign: "center",
                padding: 18,
              }}
            >
              Start live calibration to wear the configured try-on asset.
            </div>
          ) : null}
        </div>

        {liveCalibrationLoading || liveCalibrationError ? (
          <div
            style={{
              fontSize: 12,
              color: liveCalibrationError ? "#b91c1c" : "#92400e",
              fontWeight: 800,
            }}
          >
            {liveCalibrationError || liveCalibrationLoading}
          </div>
        ) : null}

        {liveDebug ? (
          <details style={{ fontSize: 12, color: "#334155" }}>
            <summary style={{ cursor: "pointer", fontWeight: 900, color: "#92400e" }}>
              Physical size calibration debug
            </summary>
            <div style={{ marginTop: 8, display: "grid", gap: 3 }}>
              <div>Configured A/B: {liveDebug.configuredMm.toFixed(2)} mm</div>
              <div>Asset A/B: {liveDebug.assetMeasurementPx.toFixed(1)} px</div>
              <div>Asset px/mm: {liveDebug.assetPixelsPerMm.toFixed(3)}</div>
              <div>Raw camera estimate: {liveDebug.rawCameraPixelsPerMm.toFixed(3)} CSS px/mm</div>
              <div>Raw image scale: {liveDebug.rawImageScale.toFixed(5)}</div>
              <div>Admin physical correction: {liveDebug.physicalScaleCorrection.toFixed(3)}x</div>
              <div>Corrected base scale: {liveDebug.correctedBaseScale.toFixed(5)}</div>
              <div>Baseline shoulder width: {liveDebug.baselineShoulderWidthPx ? liveDebug.baselineShoulderWidthPx.toFixed(1) : "collecting"} CSS px</div>
              <div>Current shoulder width: {liveDebug.currentShoulderWidthPx.toFixed(1)} CSS px</div>
              <div>Depth ratio: {liveDebug.depthRatio.toFixed(3)}</div>
              <div>Final rendered scale: {liveDebug.finalRenderedScale.toFixed(5)}</div>
              <div>Rendered A/B: {liveDebug.renderedMeasurementPx.toFixed(1)} CSS px</div>
            </div>
          </details>
        ) : null}

        {value?.physicalScaleCalibratedAt ? (
          <div style={{ fontSize: 11, color: "#92400e" }}>
            Last saved: {value.physicalScaleCalibratedAt}
          </div>
        ) : null}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {anchorMeta.map((anchor) => {
          const point = anchors[anchor.key];
          return (
            <div
              key={anchor.key}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "7px 9px",
                fontSize: 12,
                color: "#334155",
              }}
            >
              <strong>{anchor.label}</strong> {anchor.name}:{" "}
              {`${point.x.toFixed(3)}, ${point.y.toFixed(3)}`}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "end" }}>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#334155" }}>
          Photo area shape
          <select
            value={photoRegion.shape}
            onChange={(event) =>
              onChange({
                ...nextBase(),
                photoRegion: {
                  ...photoRegion,
                  shape: event.target.value as VirtualTryOnPhotoRegion["shape"],
                },
              })
            }
            style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 9px" }}
          >
            <option value="circle">Circle</option>
            <option value="ellipse">Ellipse</option>
            <option value="rect">Rectangle</option>
            <option value="custom">Custom mask</option>
          </select>
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: 12, color: "#334155" }}>
          Photo rotation
          <input
            type="number"
            step={0.5}
            value={photoRegion.rotation ?? 0}
            onChange={(event) =>
              onChange({
                ...nextBase(),
                photoRegion: {
                  ...photoRegion,
                  rotation: Number(event.target.value) || 0,
                },
              })
            }
            style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: "7px 9px", width: 120 }}
          />
        </label>
      </div>
    </div>
  );
}
